import Link from 'next/link'
import { cn } from '@ui/lib/utils'
import { Button } from '@ui/primitives/Button'
import { t } from '@/lib'

export interface PaginationProps {
  currentPage: number
  totalPages: number
  baseUrl: string
  className?: string
  dictionary: Record<string, string>
}

export function Pagination({ currentPage, totalPages, baseUrl, className, dictionary }: PaginationProps) {
  if (totalPages <= 1) return null

  const pages: (number | 'ellipsis')[] = []
  const showEllipsis = totalPages > 7

  if (showEllipsis) {
    // Show first page
    pages.push(1)

    // Show ellipsis or pages around current
    if (currentPage > 3) {
      pages.push('ellipsis')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i)
    }

    // Show ellipsis or last page
    if (currentPage < totalPages - 2) {
      pages.push('ellipsis')
    }

    // Show last page
    pages.push(totalPages)
  } else {
    // Show all pages
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  }

  return (
    <nav className={cn('flex items-center justify-center gap-1', className)} aria-label="Pagination">
      {/* Previous button */}
      {currentPage > 1 ? (
        <Link href={`${baseUrl}?page=${currentPage - 1}`}>
          <Button size="sm" variant="ghost">
            {t(dictionary, 'navigation.previous')}
          </Button>
        </Link>
      ) : (
        <Button size="sm" disabled variant="ghost">
          {t(dictionary, 'navigation.previous')}
        </Button>
      )}

      {/* Page numbers */}
      {pages.map((page, index) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className="px-2">
            ...
          </span>
        ) : (
          <Link key={page} href={`${baseUrl}?page=${page}`}>
            <Button
              variant="ghost"
              className={cn(currentPage === page ? 'bg-gray-200 text-gray-900' : '')}
              size="sm"
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </Button>
          </Link>
        )
      )}

      {/* Next button */}
      {currentPage < totalPages ? (
        <Link href={`${baseUrl}?page=${currentPage + 1}`}>
          <Button size="sm" variant="ghost">
            {t(dictionary, 'navigation.next')}
          </Button>
        </Link>
      ) : (
        <Button size="sm" disabled variant="ghost">
          {t(dictionary, 'navigation.next')}
        </Button>
      )}
    </nav>
  )
}
